import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { Component } from '@/types';
import { shouldBlockSensorControl, useComponentInteraction } from '@/components/shared/rendering/InteractionContextRuntime';
import { useDataTextValue } from '@/hooks/useDataTextValue';
import { ChartDataSource } from '@/types/chartDataSource';
import { CONTROL_THEME } from './controlThemeTokens';

// ─── Types ───────────────────────────────────────────────────────────────────

interface StepperThemeConfig {
  buttonColor: string;
  buttonTextColor: string;
  textColor: string;
  bgColor: string;
  borderColor: string;
  buttonRadius: string;
}

interface StepperProps {
  component: Component;
}

// ─── Theme Presets ───────────────────────────────────────────────────────────

const THEMES: Record<string, StepperThemeConfig> = {
  flat: {
    buttonColor: CONTROL_THEME.muted,
    buttonTextColor: CONTROL_THEME.foreground,
    textColor: CONTROL_THEME.foreground,
    bgColor: CONTROL_THEME.card,
    borderColor: CONTROL_THEME.border,
    buttonRadius: '4px',
  },
  rounded: {
    buttonColor: '#4a90d9',
    buttonTextColor: '#ffffff',
    textColor: CONTROL_THEME.foreground,
    bgColor: CONTROL_THEME.card,
    borderColor: '#4a90d9',
    buttonRadius: '50%',
  },
  pill: {
    buttonColor: '#6c63ff',
    buttonTextColor: '#ffffff',
    textColor: '#ffffff',
    bgColor: '#6c63ff20',
    borderColor: '#6c63ff',
    buttonRadius: '999px',
  },
  material: {
    buttonColor: '#1976D2',
    buttonTextColor: '#ffffff',
    textColor: CONTROL_THEME.foreground,
    bgColor: CONTROL_THEME.card,
    borderColor: CONTROL_THEME.border,
    buttonRadius: '8px',
  },
  neon: {
    buttonColor: 'transparent',
    buttonTextColor: '#00ff88',
    textColor: '#00ff88',
    bgColor: '#1a1a2e',
    borderColor: '#00ff88',
    buttonRadius: '4px',
  },
};

// ─── Size Presets ────────────────────────────────────────────────────────────

const SIZE_MAP: Record<string, { width: number; height: number; fontSize: number; btnSize: number }> = {
  sm: { width: 120, height: 36, fontSize: 14, btnSize: 28 },
  md: { width: 160, height: 44, fontSize: 16, btnSize: 36 },
  lg: { width: 200, height: 52, fontSize: 20, btnSize: 44 },
};

// ─── Component ───────────────────────────────────────────────────────────────

export const Stepper: React.FC<StepperProps> = ({ component }) => {
  const properties = (component.properties || {}) as Record<string, unknown>;

  // ─── Property Extraction ─────────────────────────────────────────────
  const themeName = (properties.theme as string) ?? 'flat';
  const sizeName = (properties.size as string) ?? 'md';
  const disabled = (properties.disabled as boolean) ?? false;
  const label = (properties.label as string) ?? '';
  const min = (properties.min as number) ?? 0;
  const max = (properties.max as number) ?? 100;
  const step = (properties.step as number) ?? 1;
  const unit = (properties.unit as string) ?? '';
  const decimals = (properties.decimals as number) ?? 0;
  const longPressAcceleration = (properties.longPressAcceleration as boolean) ?? true;

  // ─── Theme Resolution ────────────────────────────────────────────────
  const theme = useMemo(() => {
    const base = THEMES[themeName] ?? THEMES['flat'];
    return {
      ...base,
      ...(properties.buttonColor != null && { buttonColor: properties.buttonColor as string }),
      ...(properties.textColor != null && { textColor: properties.textColor as string }),
    };
  }, [themeName, properties.buttonColor, properties.textColor]);

  const sizeConfig = SIZE_MAP[sizeName] ?? SIZE_MAP['md'];

  // ─── Data Binding ────────────────────────────────────────────────────
  const dataSource = properties.dataSource as ChartDataSource | undefined;
  const templateId = properties.queryTemplateId as string | undefined;
  const valueField = (properties.valueField as string) ?? 'value';

  const { value: rawValue } = useDataTextValue({
    dataSource,
    componentId: component.id,
    valueField,
    staticValue: dataSource ? undefined : (properties.value as string | number | undefined),
    templateId,
  });

  const propertyValue = (properties.value as number) ?? 0;
  const effectiveValue = useMemo(() => {
    if (dataSource && rawValue !== null) {
      const parsed = typeof rawValue === 'number' ? rawValue : parseFloat(String(rawValue));
      return isNaN(parsed) ? propertyValue : parsed;
    }
    return propertyValue;
  }, [dataSource, rawValue, propertyValue]);

  const [localValue, setLocalValue] = useState(effectiveValue);

  // Sync local value with external changes
  useEffect(() => {
    setLocalValue(effectiveValue);
  }, [effectiveValue]);

  // ─── Interaction ─────────────────────────────────────────────────────
  const { handlers, mode } = useComponentInteraction(component);
  const isViewerMode = mode === 'functional';
  const sensorControlBlocked = shouldBlockSensorControl(component);
  const canControl = !disabled && !sensorControlBlocked && isViewerMode && Boolean(handlers.onSensorControl);

  const sensorId = useMemo(() => {
    const action = properties.action;
    if (typeof action === 'object' && action !== null) {
      return (action as Record<string, unknown>).deviceId as string || component.id;
    }
    return component.id;
  }, [properties.action, component.id]);

  const changeValue = useCallback((delta: number) => {
    if (!canControl) return;
    setLocalValue((prev) => {
      const newVal = Math.min(max, Math.max(min, prev + delta));
      handlers.onSensorControl?.({
        sensorId,
        action: 'control',
        parameters: { command: 'set', value: newVal },
      });
      return newVal;
    });
  }, [canControl, handlers, sensorId, min, max]);

  // ─── Long Press Acceleration ─────────────────────────────────────────
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number>(0);

  const startLongPress = useCallback((delta: number) => {
    if (!canControl || !longPressAcceleration) return;
    startTimeRef.current = Date.now();

    const tick = () => {
      changeValue(delta);
      const elapsed = Date.now() - startTimeRef.current;
      const delay = elapsed > 3000 ? 50 : elapsed > 1500 ? 100 : 200;
      intervalRef.current = setTimeout(tick, delay);
    };

    intervalRef.current = setTimeout(tick, 500);
  }, [canControl, longPressAcceleration, changeValue]);

  const stopLongPress = useCallback(() => {
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    };
  }, []);

  // ─── Display Value ───────────────────────────────────────────────────
  const displayValue = isViewerMode ? localValue : effectiveValue;
  const displayText = `${displayValue.toFixed(decimals)}${unit ? ` ${unit}` : ''}`;

  // ─── Styles ──────────────────────────────────────────────────────────
  const btnStyle: React.CSSProperties = {
    width: sizeConfig.btnSize,
    height: sizeConfig.btnSize,
    borderRadius: theme.buttonRadius,
    backgroundColor: theme.buttonColor,
    border: `1px solid ${theme.borderColor}`,
    color: theme.buttonTextColor,
    fontSize: sizeConfig.fontSize * 1.2,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: canControl ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'all 0.1s ease',
    userSelect: 'none',
    flexShrink: 0,
    lineHeight: 1,
  };

  return (
    <div
      data-testid="stepper"
      role="spinbutton"
      aria-valuenow={displayValue}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-label={label || 'Stepper'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        gap: 6,
        minWidth: 0,
        minHeight: 0,
        padding: 4,
        boxSizing: 'border-box',
        userSelect: 'none',
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        backgroundColor: theme.bgColor,
        borderRadius: '8px',
        border: `1px solid ${theme.borderColor}`,
        padding: '3px 5px',
        height: sizeConfig.height,
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}>
        {/* Decrement button */}
        <div
          data-testid="stepper-decrement"
          role="button"
          aria-label="Decrement"
          onClick={() => changeValue(-step)}
          onPointerDown={() => startLongPress(-step)}
          onPointerUp={stopLongPress}
          onPointerLeave={stopLongPress}
          style={{
            ...btnStyle,
            opacity: disabled || displayValue <= min ? 0.4 : 1,
          }}
        >
          −
        </div>

        {/* Value display */}
        <div
          data-testid="stepper-value"
          style={{
            minWidth: sizeConfig.width - sizeConfig.btnSize * 2 - 24,
            textAlign: 'center',
            fontSize: sizeConfig.fontSize,
            fontWeight: 600,
            color: theme.textColor,
            fontVariantNumeric: 'tabular-nums',
            lineHeight: 1,
            padding: '0 4px',
          }}
        >
          {displayText}
        </div>

        {/* Increment button */}
        <div
          data-testid="stepper-increment"
          role="button"
          aria-label="Increment"
          onClick={() => changeValue(step)}
          onPointerDown={() => startLongPress(step)}
          onPointerUp={stopLongPress}
          onPointerLeave={stopLongPress}
          style={{
            ...btnStyle,
            opacity: disabled || displayValue >= max ? 0.4 : 1,
          }}
        >
          +
        </div>
      </div>

      {/* Label */}
      {label && (
        <span style={{
          fontSize: 11,
          color: CONTROL_THEME.mutedForeground,
          textAlign: 'center',
          lineHeight: 1.2,
          maxWidth: sizeConfig.width,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {label}
        </span>
      )}
    </div>
  );
};
